/**
 * Admin-only API client. Token is held in localStorage and sent on every
 * request as `Authorization: Bearer <token>`. On 401/404 we clear the
 * token and redirect to /admin/login.
 */
const TOKEN_KEY = 'dormant.admin.token';

export interface AdminOrderShared {
  archived?: boolean;
  archivedAt?: string | null;
  archivedReason?: string | null;
}

export interface AdminOrder extends AdminOrderShared {
  id: string;
  service: 'DORMANT_ACCOUNTS' | 'CONFIRMATION_STATEMENT';
  metadata: Record<string, unknown> | null;
  email: string;
  companyNumber: string;
  companyName: string;
  directorName: string;
  isFirstFiling: boolean;
  periodStart: string;
  periodEnd: string;
  amountPence: number;
  status: 'PENDING_PAYMENT' | 'PAID' | 'SUBMITTING' | 'ACCEPTED' | 'REJECTED' | 'COMPLETED';
  figures: {
    fixedAssets: number;
    currentAssets: number;
    creditorsDueWithinOneYear: number;
    creditorsDueAfterOneYear: number;
    calledUpShareCapital: number;
    profitAndLossAccount: number;
    netAssets: number;
  };
  stripeSessionId: string | null;
  stripePaymentIntentId: string | null;
  chSubmissionId: string | null;
  chAckNumber: string | null;
  errorReason: string | null;
  createdAt: string;
  paidAt: string | null;
  submittedAt: string | null;
  leadDisplayId?: string | null;
  businessDisplayId?: string | null;
  businessFilings?: number;
  businessLifetimePence?: number;
}

export interface ListResponse {
  items: AdminOrder[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CustomerSummary {
  email: string;
  displayName: string | null;
  totalFilings: number;
  acceptedCount: number;
  lifetimePence: number;
  companyCount: number;
  lastOrderAt: string;
  archived: boolean;
}

export interface CustomerOrderSummary {
  id: string;
  companyName: string;
  companyNumber: string;
  directorName: string;
  periodStart: string;
  periodEnd: string;
  status: AdminOrder['status'];
  amountPence: number;
  chAckNumber: string | null;
  errorReason: string | null;
  createdAt: string;
  paidAt: string | null;
  submittedAt: string | null;
}

export interface CustomerCompany {
  companyName: string;
  companyNumber: string;
  filings: number;
  lastFiledAt: string | null;
  archived: boolean;
  displayId: string | null;
  lifetimePence: number;
}

export interface CustomerDetail {
  email: string;
  displayName: string | null;
  leadDisplayId: string | null;
  totalFilings: number;
  acceptedCount: number;
  lifetimePence: number;
  archived: boolean;
  archivedAt: string | null;
  archivedReason: string | null;
  companies: CustomerCompany[];
  orders: CustomerOrderSummary[];
}

export interface BlogPostSummary {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  author: string;
  status: 'DRAFT' | 'PUBLISHED';
  publishedAt: string | null;
  updatedAt: string;
}

export interface BlogPost extends BlogPostSummary {
  body: string;
  coverImageUrl: string | null;
  createdAt: string;
}

export interface FollowUp {
  id: string;
  orderId: string;
  dueDate: string;            // ISO yyyy-mm-dd
  reason: string;
  status: 'PENDING' | 'COMPLETED' | 'DISMISSED';
  autoCreated: boolean;
  completedAt: string | null;
  completedNote: string | null;
  createdAt: string;
  // Present on list responses (joined from orders); absent on single-row writes.
  companyName?: string;
  companyNumber?: string;
  orderStatus?: AdminOrder['status'];
  customerEmail?: string;
}

export interface EmailLogEntry {
  id: string;
  toAddress: string;
  fromAddress: string;
  subject: string;
  template: string;
  direction: 'OUTBOUND_CUSTOMER' | 'OUTBOUND_ADMIN';
  resendId: string | null;
  status: string;
  errorMessage: string | null;
  orderId: string | null;
  customerEmail: string | null;
  sentAt: string;
}

export type LeadStatus = 'NEW' | 'CONTACTED' | 'REPLIED' | 'WON' | 'LOST';
export type LeadSource =
  | 'COLD_EMAIL'
  | 'CONTACT_FORM'
  | 'WIZARD_ABANDON'
  | 'REFERRAL'
  | 'OTHER';

export interface Business {
  id: string;
  displayId: string | null;
  leadId: string;
  companyName: string;
  companyNumber: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Lead {
  id: string;
  displayId: string | null;
  name: string;
  email: string;
  phone: string | null;
  companyName: string | null;
  companyNumber: string | null;
  source: LeadSource;
  status: LeadStatus;
  notes: string | null;
  lastContactedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // Enriched fields from GET /leads response:
  businesses?: Business[];
  isPaid?: boolean;
}

export interface AdminSettings {
  price: { pence: number; display: string };
  support: { notifyEmail: string | null; fromEmail: string | null };
  companiesHouse: {
    mode: 'TEST' | 'LIVE';
    presenterIdSet: boolean;
    gatewayUrlSet: boolean;
  };
  adminPassword: { source: 'database' | 'environment'; usingDefault: boolean };
}

export interface StatsResponse {
  countsByStatus: Record<string, number>;
  revenue: {
    totalPence: number;
    last7dPence: number;
    last30dPence: number;
  };
}

export const adminAuth = {
  get token(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },
  set token(v: string | null) {
    if (v) localStorage.setItem(TOKEN_KEY, v);
    else localStorage.removeItem(TOKEN_KEY);
  },
  isLoggedIn(): boolean {
    return !!this.token;
  },
  logout(): void {
    this.token = null;
  },
};

async function adminFetch<T>(
  path: string,
  init?: { method?: string; body?: unknown },
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  const t = adminAuth.token;
  if (t) headers.Authorization = `Bearer ${t}`;
  if (init?.body) headers['Content-Type'] = 'application/json';

  const res = await fetch(path, {
    method: init?.method || 'GET',
    headers,
    body: init?.body ? JSON.stringify(init.body) : undefined,
  });

  if (res.status === 404 && t) {
    // Token has expired or signing secret rotated – log out.
    adminAuth.logout();
    if (typeof window !== 'undefined' && !window.location.pathname.endsWith('/admin/login')) {
      window.location.href = '/admin/login';
    }
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((data as { error?: string })?.error || `Request failed (${res.status})`);
  }
  return data as T;
}

export const adminApi = {
  login: (password: string, totpCode?: string) =>
    adminFetch<{ token?: string; needsTotp?: boolean }>('/api/admin/login', {
      method: 'POST',
      body: totpCode ? { password, totpCode } : { password },
    }),

  // ── Two-factor authentication ────────────────────────────────────────
  twoFactorStatus: () =>
    adminFetch<{ enabled: boolean }>('/api/admin/2fa/status'),
  twoFactorSetup: () =>
    adminFetch<{ secret: string; otpauthUrl: string; qrCodeDataUrl: string }>(
      '/api/admin/2fa/setup',
      { method: 'POST', body: {} },
    ),
  twoFactorEnable: (body: { secret: string; code: string }) =>
    adminFetch<{ ok: true }>('/api/admin/2fa/enable', { method: 'POST', body }),
  twoFactorDisable: (body: { password: string; code: string }) =>
    adminFetch<{ ok: true }>('/api/admin/2fa/disable', { method: 'POST', body }),
  listOrders: (
    params: {
      status?: string;
      q?: string;
      page?: number;
      pageSize?: number;
      archive?: 'active' | 'archived' | 'all';
      hideTest?: boolean;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') qs.set(k, String(v));
    }
    return adminFetch<ListResponse>(`/api/admin/orders?${qs.toString()}`);
  },
  archiveOrder: (id: string, opts: { reason?: string } = {}) =>
    adminFetch<{ ok: true }>(
      `/api/admin/orders/${encodeURIComponent(id)}/archive`,
      { method: 'POST', body: opts },
    ),
  unarchiveOrder: (id: string) =>
    adminFetch<{ ok: true }>(
      `/api/admin/orders/${encodeURIComponent(id)}/unarchive`,
      { method: 'POST', body: {} },
    ),
  /**
   * Hard-delete a TEST-* order. Backend refuses to delete real
   * (ord_*) orders — those can only be archived. Related follow-ups
   * cascade-delete via the DB foreign key.
   */
  deleteTestOrder: (id: string) =>
    adminFetch<{ ok: true }>(
      `/api/admin/orders/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),
  /**
   * Fetch the upcoming-filings calendar. For every customer we've
   * accepted a filing for, hits CH's public API to get the NEXT
   * accounts' due date. Sorted overdue-first, then by due date asc.
   */
  upcomingFilings: () =>
    adminFetch<{
      items: Array<{
        companyNumber: string;
        companyName: string;
        email: string;
        lastPeriodEnd: string;
        nextPeriodEnd: string | null;
        nextDueOn: string | null;
        overdue: boolean;
        lookupFailed: boolean;
      }>;
    }>('/api/admin/upcoming-filings'),
  getOrder: (id: string) => adminFetch<AdminOrder>(`/api/admin/orders/${encodeURIComponent(id)}`),
  resubmit: (id: string, authCode: string) =>
    adminFetch<{ ok: true; orderId: string }>(
      `/api/admin/orders/${encodeURIComponent(id)}/resubmit`,
      { method: 'POST', body: { authCode } },
    ),
  updateFigures: (
    id: string,
    patch: Partial<AdminOrder['figures']>,
  ) =>
    adminFetch<{ ok: true; order: AdminOrder }>(
      `/api/admin/orders/${encodeURIComponent(id)}/figures`,
      { method: 'PATCH', body: patch },
    ),
  /**
   * Fetch the stored (or reconstructed) request/response XML
   * exchange for a specific order and open it in a new browser tab
   * as a plain-text diagnostic view. Used to share XML with
   * Companies House XML Gateway support when they ask for it.
   */
  fetchRawXml: async (orderId: string) => {
    const t = adminAuth.token;
    const res = await fetch(`/api/admin/orders/${encodeURIComponent(orderId)}/raw-xml`, {
      method: 'GET',
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Fetch raw XML failed (HTTP ${res.status})`);
    }
    type XmlPair = {
      request: string | null;
      response: string | null;
      capturedAt: string | null;
      source?: string | null;
      status?: string | null;
    };
    const payload = (await res.json()) as {
      source: 'stored' | 'reconstructed';
      initialPost: XmlPair | null;
      poll: XmlPair | null;
      note?: string;
      originalSubmissionNumber?: string | null;
    };
    // Render both stages in a plain viewer new-tab so operators can
    // copy either into an email to CH support. Escape angle-brackets so
    // the XML displays literally rather than being interpreted as HTML.
    const esc = (s: string | null | undefined) =>
      (s ?? '(none)')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    const renderStage = (title: string, pair: XmlPair | null, subtitle: string) => {
      if (!pair) return '';
      const metaBits: string[] = [];
      if (pair.capturedAt) metaBits.push(`Captured ${esc(pair.capturedAt)}`);
      if (pair.source) metaBits.push(`Source ${esc(pair.source)}`);
      if (pair.status) metaBits.push(`Status ${esc(pair.status)}`);
      return `
<section style="margin-top:32px">
  <h2 style="border-bottom:1px solid #e2e8f0;padding-bottom:6px">${esc(title)}</h2>
  <div style="font-size:12px;color:#64748b;margin-bottom:12px">${esc(subtitle)}${metaBits.length ? ' · ' + metaBits.join(' · ') : ''}</div>
  <h3>Request (envelope POSTed to CH)</h3>
  <pre>${esc(pair.request)}</pre>
  <h3>Response (XML CH returned)</h3>
  <pre>${esc(pair.response)}</pre>
</section>`;
    };
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Raw XML - ${orderId}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, sans-serif; margin: 24px; color: #0f172a; }
  h1 { font-size: 20px; margin: 0 0 8px; }
  h2 { font-size: 15px; margin: 24px 0 6px; }
  h3 { font-size: 13px; margin: 16px 0 6px; color: #334155; }
  .meta { font-size: 13px; color: #64748b; margin-bottom: 24px; }
  .meta strong { color: #0f172a; }
  .badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-weight: 600; font-size: 11px; text-transform: uppercase; }
  .badge.stored { background: #dcfce7; color: #166534; }
  .badge.reconstructed { background: #fef3c7; color: #92400e; }
  pre { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; overflow-x: auto; font-size: 12px; line-height: 1.5; white-space: pre-wrap; word-break: break-all; }
  .note { background: #fef3c7; border-left: 4px solid #f59e0b; padding: 10px 14px; margin: 12px 0 24px; font-size: 13px; color: #78350f; }
  .empty { color: #94a3b8; font-style: italic; margin-top: 16px; font-size: 13px; }
</style></head><body>
<h1>Raw CH Gateway exchange</h1>
<div class="meta">
  Order <strong>${esc(orderId)}</strong>
  ${payload.originalSubmissionNumber ? ` · SubmissionNumber <strong>${esc(payload.originalSubmissionNumber)}</strong>` : ''}
  · Source <span class="badge ${payload.source}">${payload.source}</span>
</div>
${payload.note ? `<div class="note">${esc(payload.note)}</div>` : ''}
${renderStage('Initial submission', payload.initialPost, 'The envelope we POSTed to open the filing, and the sync response CH returned.')}
${renderStage('Poll follow-up', payload.poll, 'GetSubmissionStatus query made after the initial POST + the terminal response CH returned.')}
${!payload.initialPost && !payload.poll ? '<div class="empty">No stored or reconstructable XML for this order.</div>' : ''}
</body></html>`;
    const w = window.open('', '_blank');
    if (!w) throw new Error('Popup blocked - allow popups for this site and retry');
    w.document.open();
    w.document.write(html);
    w.document.close();
  },

  /**
   * Fetch the iXBRL preview for a hypothetical filing and open it
   * in a new browser tab as rendered XHTML. Nothing is stored on
   * the server. Used by both the File Now page and the Edit Figures
   * panel to review the balance-sheet layout before submitting.
   */
  previewIxbrl: async (input: {
    companyNumber: string;
    companyName: string;
    directorName?: string;
    periodStart: string;
    periodEnd: string;
    isFirstFiling: boolean;
    figures: AdminOrder['figures'];
  }) => {
    const t = adminAuth.token;
    const res = await fetch('/api/admin/preview-ixbrl', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(t ? { Authorization: `Bearer ${t}` } : {}),
      },
      body: JSON.stringify(input),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Preview failed (HTTP ${res.status})`);
    }
    const xhtml = await res.text();
    const w = window.open('', '_blank');
    if (!w) throw new Error('Popup blocked — allow popups for this site and retry');
    w.document.open();
    w.document.write(xhtml);
    w.document.close();
  },
  resendReceipt: (id: string) =>
    adminFetch<{ ok: true; sentTo: string }>(
      `/api/admin/orders/${encodeURIComponent(id)}/resend-receipt`,
      { method: 'POST', body: {} },
    ),
  sendFilingEmail: (id: string) =>
    adminFetch<{ ok: true; sentTo: string; type: 'ACCEPTED' | 'REJECTED' }>(
      `/api/admin/orders/${encodeURIComponent(id)}/send-filing-email`,
      { method: 'POST', body: {} },
    ),
  markManuallyFiled: (id: string, body: { chAckNumber?: string; note?: string } = {}) =>
    adminFetch<{ ok: true; order: AdminOrder }>(
      `/api/admin/orders/${encodeURIComponent(id)}/mark-manually-filed`,
      { method: 'POST', body },
    ),
  refundOrder: (id: string, opts: { amountPence?: number; reason?: string } = {}) =>
    adminFetch<{ ok: true; refundId: string; amountPence: number; status: string }>(
      `/api/admin/orders/${encodeURIComponent(id)}/refund`,
      { method: 'POST', body: opts },
    ),
  fileNow: (body: {
    email: string;
    companyNumber: string;
    companyName: string;
    directorName: string;
    isFirstFiling: boolean;
    periodStart: string;
    periodEnd: string;
    authCode: string;
    figures: {
      fixedAssets: number;
      currentAssets: number;
      creditorsDueWithinOneYear: number;
      creditorsDueAfterOneYear: number;
      calledUpShareCapital: number;
      profitAndLossAccount: number;
      netAssets: number;
    };
    reason?: string;
  }) =>
    adminFetch<{ ok: true; orderId: string }>('/api/admin/file-now', {
      method: 'POST',
      body,
    }),

  stats: () => adminFetch<StatsResponse>('/api/admin/stats'),

  // ── Leads ────────────────────────────────────────────────────────────
  listLeads: (params: { status?: LeadStatus; q?: string } = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') qs.set(k, String(v));
    }
    return adminFetch<{ items: Lead[] }>(`/api/admin/leads?${qs.toString()}`);
  },
  createLead: (body: {
    name: string;
    email: string;
    phone?: string;
    companyName?: string;
    companyNumber?: string;
    source?: LeadSource;
    status?: LeadStatus;
    notes?: string;
  }) =>
    adminFetch<Lead>('/api/admin/leads', { method: 'POST', body }),
  updateLead: (
    id: string,
    body: Partial<{
      name: string;
      email: string;
      phone: string;
      companyName: string;
      companyNumber: string;
      source: LeadSource;
      status: LeadStatus;
      notes: string;
    }>,
  ) => adminFetch<Lead>(`/api/admin/leads/${encodeURIComponent(id)}`, { method: 'PATCH', body }),
  deleteLead: (id: string) =>
    adminFetch<{ ok: true }>(`/api/admin/leads/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // Businesses under a lead
  addBusiness: (
    leadId: string,
    body: { companyName: string; companyNumber?: string; notes?: string },
  ) =>
    adminFetch<Business>(
      `/api/admin/leads/${encodeURIComponent(leadId)}/businesses`,
      { method: 'POST', body },
    ),
  updateBusiness: (
    id: string,
    body: Partial<{ companyName: string; companyNumber: string; notes: string }>,
  ) => adminFetch<Business>(`/api/admin/businesses/${encodeURIComponent(id)}`, { method: 'PATCH', body }),
  deleteBusiness: (id: string) =>
    adminFetch<{ ok: true }>(`/api/admin/businesses/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  bulkImportLeads: (body: {
    rows: Array<{
      name: string;
      email: string;
      phone?: string;
      companyName?: string;
      companyNumber?: string;
      notes?: string;
    }>;
    source?: LeadSource;
  }) =>
    adminFetch<{
      imported: number;
      skippedDup: number;
      errors: { row: number; reason: string }[];
    }>('/api/admin/leads/bulk-import', { method: 'POST', body }),

  // ── Email log ────────────────────────────────────────────────────────
  listEmails: (params: { customerEmail?: string; orderId?: string }) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') qs.set(k, String(v));
    }
    return adminFetch<{ items: EmailLogEntry[] }>(`/api/admin/emails?${qs.toString()}`);
  },

  // Customers
  listCustomers: (
    params: { q?: string; page?: number; pageSize?: number; archive?: 'active' | 'archived' | 'all' } = {},
  ) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') qs.set(k, String(v));
    return adminFetch<{ items: CustomerSummary[]; total: number; page: number; pageSize: number }>(`/api/admin/customers?${qs.toString()}`);
  },
  getCustomer: (email: string) =>
    adminFetch<CustomerDetail>(`/api/admin/customers/${encodeURIComponent(email)}`),
  archiveCustomer: (
    email: string,
    opts: { companyNumber?: string; reason?: string } = {},
  ) =>
    adminFetch<{ ok: true }>(
      `/api/admin/customers/${encodeURIComponent(email)}/archive`,
      { method: 'POST', body: opts },
    ),
  unarchiveCustomer: (email: string, opts: { companyNumber?: string } = {}) =>
    adminFetch<{ ok: true }>(
      `/api/admin/customers/${encodeURIComponent(email)}/unarchive`,
      { method: 'POST', body: opts },
    ),

  // Blog
  listPosts: () => adminFetch<{ items: BlogPostSummary[] }>('/api/admin/posts'),
  getPost: (slug: string) => adminFetch<BlogPost>(`/api/admin/posts/${encodeURIComponent(slug)}`),
  createPost: (body: {
    slug: string;
    title: string;
    excerpt?: string;
    body: string;
    coverImageUrl?: string;
    author?: string;
    status?: 'DRAFT' | 'PUBLISHED';
  }) => adminFetch<BlogPost>('/api/admin/posts', { method: 'POST', body }),
  updatePost: (slug: string, body: Partial<{
    slug: string; title: string; excerpt: string | null; body: string;
    coverImageUrl: string | null; author: string; status: 'DRAFT' | 'PUBLISHED';
  }>) => adminFetch<BlogPost>(`/api/admin/posts/${encodeURIComponent(slug)}`, { method: 'PATCH', body }),
  deletePost: (slug: string) =>
    adminFetch<{ ok: true }>(`/api/admin/posts/${encodeURIComponent(slug)}`, { method: 'DELETE' }),

  // Follow-ups (CRM)
  listFollowUps: (scope: 'today' | 'upcoming' | 'all' | 'completed' = 'today', orderId?: string) => {
    const q = new URLSearchParams({ scope });
    if (orderId) q.set('orderId', orderId);
    return adminFetch<{ items: FollowUp[] }>(`/api/admin/followups?${q.toString()}`);
  },
  createFollowUp: (orderId: string, body: { dueDate: string; reason: string }) =>
    adminFetch<FollowUp>(`/api/admin/orders/${encodeURIComponent(orderId)}/followups`, {
      method: 'POST',
      body,
    }),
  updateFollowUp: (
    id: string,
    body: Partial<{ dueDate: string; reason: string; status: FollowUp['status']; completedNote: string }>,
  ) =>
    adminFetch<FollowUp>(`/api/admin/followups/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body,
    }),
  deleteFollowUp: (id: string) =>
    adminFetch<{ ok: true }>(`/api/admin/followups/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  // Image upload (Supabase Storage)
  uploadImage: (body: { filename?: string; contentType: string; dataBase64: string }) =>
    adminFetch<{ publicUrl: string; path: string }>('/api/admin/upload-image', {
      method: 'POST',
      body,
    }),

  // Settings
  getSettings: () => adminFetch<AdminSettings>('/api/admin/settings'),
  changePassword: (body: { currentPassword: string; newPassword: string; confirmPassword: string }) =>
    adminFetch<{ ok: true }>('/api/admin/change-password', { method: 'POST', body }),
};
